// client/src/components/agendamentos/CardEvento.js
import React from 'react';
import { User, Lock, Briefcase, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';

const extrairIdReal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj.$oid) return obj.$oid;
    if (obj._id) {
        if (typeof obj._id === 'string') return obj._id;
        if (obj._id.$oid) return obj._id.$oid;
        return obj._id.toString();
    }
    if (obj.id) {
        if (typeof obj.id === 'string') return obj.id;
        if (obj.id.$oid) return obj.id.$oid;
        return obj.id.toString();
    }
    return null;
};

const parseDateSafe = (d) => {
    if (!d) return new Date();
    if (typeof d === 'string' || typeof d === 'number') return new Date(d);
    if (d.$date) return new Date(d.$date);
    return new Date(d);
};

// ✅ Função para formatar itens com quantidade
const formatarItensComQuantidade = (itens) => {
    if (!itens || itens.length === 0) return 'Nenhum item';
    
    return itens.map(item => {
        const nome = item.item?.nome || item.descricao || 'Item';
        const quantidade = item.quantidade || 1;
        
        if (quantidade > 1) {
            return `${quantidade}x ${nome}`;
        }
        return nome;
    }).join(' + ');
};

const CardEvento = ({ 
    evento, 
    onEventClick, 
    coluna = 0, 
    totalColunas = 1, 
    pixelsPorMinuto = 1.5, 
    horaInicio = 0, 
    clientes,
    recursos
}) => {
    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();

    const getStatusClass = (status) => {
        const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
        switch (statusNormalized) {
            case 'confirmado': 
            case 'concluido': 
                return 'status-green';
            case 'agendado': 
                return 'status-blue';
            case 'pendente': 
                return 'status-yellow';
            case 'cancelado':
            case 'noshow':
                return 'status-red';
            default:
                return 'status-gray';
        }
    };

    const getBadgeClass = (status) => {
        const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
        switch (statusNormalized) {
            case 'confirmado':
            case 'concluido':
                return 'badge-green';
            case 'agendado':
                return 'badge-blue';
            case 'pendente':
                return 'badge-yellow';
            case 'cancelado':
            case 'noshow':
                return 'badge-red';
            default:
                return 'badge-gray';
        }
    };

    // ✅ 'NoShow' é o valor salvo no banco (ver models/Agendamento.js) — sem
    // rótulo em português aqui, o badge mostraria "NoShow" cru.
    const getStatusLabel = (status) => status === 'NoShow' ? 'Não Compareceu' : status;
    
    const formatTime = (dateString) => {
        if (!dateString) return '';
        try {
            return parseDateSafe(dateString).toLocaleTimeString('pt-BR', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        } catch (e) { 
            return 'Inválido'; 
        }
    };

    // Para posicionamento, usa as datas recortadas ao dia (presentes em eventos multi-day)
    const inicioRender = parseDateSafe(evento._renderDataInicio || evento.dataInicio);
    const fimRender    = parseDateSafe(evento._renderDataFim    || evento.dataFim);

    const minutosDesdeInicioDaGrade =
        (inicioRender.getHours() * 60 + inicioRender.getMinutes()) -
        (horaInicio * 60);

    const duracaoRealEmMinutos = (fimRender.getTime() - inicioRender.getTime()) / 60000;
    const duracaoMinimaVisual  = 20;
    const duracaoParaCalculo   = Math.max(duracaoRealEmMinutos, duracaoMinimaVisual);

    // Se o evento começa antes do início da grade (continuação de dia anterior), topo = 0
    const top = Math.max(0, minutosDesdeInicioDaGrade * pixelsPorMinuto);

    // Ajusta altura para descontar a parte acima da grade
    const minutosAcimaGrade = minutosDesdeInicioDaGrade < 0 ? Math.abs(minutosDesdeInicioDaGrade) : 0;
    const height = Math.max(duracaoMinimaVisual * pixelsPorMinuto, (duracaoParaCalculo - minutosAcimaGrade) * pixelsPorMinuto);

    const width = 100 / totalColunas;
    const left = coluna * width;
    
    const estiloDoCard = { 
        top: `${top}px`, 
        height: `${height}px`,
        width: `calc(${width}% - 4px)`,
        left: `${left}%`
    };
    
    const isBloqueio = evento.type === 'bloqueio';
    const isMultiDay = !!evento._isMultiDaySegment;
    const continuaDiaAnterior = isMultiDay && !evento._isFirstDay;
    const continuaProximoDia  = isMultiDay && !evento._isLastDay;
    
    const participanteId = extrairIdReal(evento.participante);
    const clienteNome = participanteId ? 
        ((clientes || []).find(c => extrairIdReal(c._id) === participanteId)?.user?.name || 'Cliente não informado') : 
        'Cliente não informado';
    
    // ✅ USAR A NOVA FUNÇÃO para formatar itens com quantidade
    const servicosNomes = formatarItensComQuantidade(evento.itens);

    // ✅ Calcular quantidade total de itens (para mostrar badge)
    const quantidadeTotal = (evento.itens || []).reduce((acc, item) => acc + (item.quantidade || 1), 0);
    const mostrarBadgeQuantidade = quantidadeTotal > 1;

    // Controle de Sessão (Servico.controlaSessao) e Controle de Pacotes
    // (PacoteCliente) — dois indicadores independentes, ambos opcionais.
    const itemComSessao = (evento.itens || []).find(item => item.numeroSessao);
    const dadosPacote = evento.metadataPacote?.utilizado ? evento.metadataPacote.dadosPacote : null;
    const sessaoDoPacote = dadosPacote
        ? Math.max(1, (dadosPacote.sessoesTotais || 0) - (dadosPacote.sessoesRestantes || 0))
        : null;

    return (
        <div 
            style={estiloDoCard} 
            className={`card-evento ${isBloqueio ? 'is-bloqueio' : getStatusClass(evento.status)}`}
            onClick={() => onEventClick(evento)}
        >
            <div className="card-evento-content">
                {continuaDiaAnterior && (
                    <p className="card-multiday-indicator" title={`Entrada: ${formatTime(evento.dataInicio)}`}>
                        <ChevronUp size={10} /> Cont. dia anterior
                    </p>
                )}
                {isBloqueio ? (
                    <div className="card-main-info">
                        <p className="card-evento-horario">
                            <Clock size={12} className="icon" />
                            {evento.diaInteiro ? 'Dia Inteiro' : `${formatTime(evento.dataInicio)} - ${formatTime(evento.dataFim)}`}
                        </p>
                        <p className="card-evento-titulo">
                            <Lock size={12} className="icon" /> {evento.motivo}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="card-main-info">
                            <p className="card-evento-titulo">
                                <User size={12} className="icon" /> {clienteNome}
                            </p>
                            <p className="card-evento-detalhe">
                                <Briefcase size={12} className="icon" /> {servicosNomes}
                            </p>
                            {mostrarBadgeQuantidade && (
                                <span className="quantidade-badge" title={`${quantidadeTotal} ${quantidadeTotal === 1 ? 'item' : 'itens'} no total`}>
                                    {quantidadeTotal} itens
                                </span>
                            )}
                            {(itemComSessao || dadosPacote || evento.sinal?.exigido) && (
                                <div className="card-badges-row">
                                    {itemComSessao && (
                                        <span className="badge-sessao" title="Controle de Sessão">
                                            Sessão {itemComSessao.numeroSessao}
                                        </span>
                                    )}
                                    {dadosPacote && (
                                        <span className="badge-pacote" title={`Controle de Pacote — ${dadosPacote.nome || 'Pacote'}`}>
                                            Pacote {sessaoDoPacote}/{dadosPacote.sessoesTotais || 0}
                                        </span>
                                    )}
                                    {evento.sinal?.exigido && (
                                        evento.sinal.status === 'Pago' ? (
                                            <span className="badge-sinal-pago" title="Sinal pago">
                                                Sinal pago
                                            </span>
                                        ) : (
                                            <span className="badge-sinal-pendente" title={`Sinal pendente — R$ ${Number(evento.sinal.valor || 0).toFixed(2)}`}>
                                                Sinal pendente
                                            </span>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="card-meta-info">
                            <p className="card-evento-horario">
                                <Clock size={12} className="icon" />
                                {formatTime(evento.dataInicio)} - {formatTime(evento.dataFim)}
                            </p>
                            {evento.financeiro?.total != null && (
                               <span className="card-evento-valor">
                                   {formatCurrency(evento.financeiro.total, currency, locale)}
                               </span>
                            )}
                            <span className={`badge ${getBadgeClass(evento.status)}`}>
                                {getStatusLabel(evento.status)}
                            </span>
                        </div>
                    </>
                )}
                {continuaProximoDia && (
                    <p className="card-multiday-indicator" title={`Saída: ${formatTime(evento.dataFim)}`}>
                        <ChevronDown size={10} /> Cont. próximo dia
                    </p>
                )}
            </div>
        </div>
    );
};

export default CardEvento;